import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingManagementService, PlatformBooking } from '../services/bookingManagement';
import './BookingDetailPage.css';

type Tab = 'overview' | 'negotiations' | 'payments' | 'timeline';

export const BookingDetailPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<PlatformBooking | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [negotiation, setNegotiation] = useState<unknown | null>(null);
  const [payments, setPayments] = useState<unknown[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!bookingId) return;

    const loadBooking = async () => {
      try {
        setLoading(true);
        setError(null);
        const bookingData = await bookingManagementService.getBooking(bookingId);
        setBooking(bookingData);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load booking');
        console.error('Error loading booking:', err);
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId || !booking || activeTab === 'overview' || activeTab === 'timeline') return;

    const loadTabData = async () => {
      try {
        setLoadingTab(true);
        switch (activeTab) {
          case 'negotiations': {
            const negotiationsData = await bookingManagementService.getBookingNegotiations(bookingId);
            setNegotiation(negotiationsData.negotiation);
            break;
          }
          case 'payments': {
            const paymentsData = await bookingManagementService.getBookingPayments(bookingId);
            setPayments(paymentsData.payments);
            break;
          }
        }
      } catch (err: unknown) {
        console.error(`Error loading ${activeTab}:`, err);
      } finally {
        setLoadingTab(false);
      }
    };

    loadTabData();
  }, [activeTab, bookingId, booking]);

  const handleCancel = async () => {
    if (!bookingId) return;

    if (!confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setCancelling(true);
      const updatedBooking = await bookingManagementService.cancelBooking(bookingId);
      setBooking(updatedBooking);
    } catch (err: unknown) {
      console.error('Error cancelling booking:', err);
      alert('Failed to cancel booking: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setCancelling(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!bookingId) return;

    try {
      const updatedBooking = await bookingManagementService.updateBooking(bookingId, {
        status: newStatus as PlatformBooking['status'],
      });
      setBooking(updatedBooking);
    } catch (err: unknown) {
      console.error('Error updating booking status:', err);
      alert('Failed to update booking status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading">Loading booking...</div>;
  }

  if (error || !booking) {
    return (
      <div className="error-container">
        <div className="error-message">{error || 'Booking not found'}</div>
        <button onClick={() => navigate('/dashboard/bookings')} className="back-button">
          Back to Bookings
        </button>
      </div>
    );
  }

  return (
    <div className="booking-detail-page">
      <div className="booking-detail-header">
        <button onClick={() => navigate('/dashboard/bookings')} className="back-button">
          ← Back to Bookings
        </button>
        <h1>Booking Details</h1>
      </div>

      <div className="booking-info-card">
        <div className="booking-info-header">
          <div>
            <h2>Booking #{booking.bookingId.substring(0, 8)}</h2>
            <p className="booking-date">
              Scheduled: {formatDate(booking.scheduledDate)} at {booking.scheduledTime}
            </p>
          </div>
          <div className="booking-actions">
            <span className={`status-badge ${booking.status}`}>
              {booking.status}
            </span>
            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
              <>
                <button
                  className="cancel-button"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Booking'}
                </button>
                <select
                  className="status-select"
                  value={booking.status}
                  onChange={(e) => handleStatusUpdate(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </>
            )}
          </div>
        </div>

        <div className="booking-stats-grid">
          <div className="stat-card">
            <div className="stat-value">₹{booking.pricing.rate.toLocaleString()}</div>
            <div className="stat-label">Total Amount</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              <span className={`payment-status-badge ${booking.paymentStatus}`}>
                {booking.paymentStatus}
              </span>
            </div>
            <div className="stat-label">Payment Status</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{booking.serviceType}</div>
            <div className="stat-label">Service Type</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {booking.negotiationStatus !== 'none' ? booking.negotiationStatus : 'None'}
            </div>
            <div className="stat-label">Negotiation</div>
          </div>
        </div>

        <div className="booking-details-grid">
          <div className="detail-item">
            <label>Booking ID</label>
            <div>{booking.bookingId}</div>
          </div>
          <div className="detail-item">
            <label>Customer ID</label>
            <div>{booking.customerId}</div>
          </div>
          <div className="detail-item">
            <label>Provider ID</label>
            <div>{booking.providerId}</div>
          </div>
          <div className="detail-item">
            <label>Original Rate</label>
            <div>₹{booking.originalRate.toLocaleString()}</div>
          </div>
          {booking.finalRate && (
            <div className="detail-item">
              <label>Final Rate</label>
              <div>₹{booking.finalRate.toLocaleString()}</div>
            </div>
          )}
          {booking.duration && (
            <div className="detail-item">
              <label>Duration</label>
              <div>{booking.duration} hours</div>
            </div>
          )}
          <div className="detail-item">
            <label>Created</label>
            <div>{formatDate(booking.createdAt)}</div>
          </div>
          <div className="detail-item">
            <label>Last Updated</label>
            <div>{formatDate(booking.updatedAt)}</div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'negotiations' ? 'active' : ''}`}
            onClick={() => setActiveTab('negotiations')}
          >
            Negotiations ({negotiation ? 1 : 0})
          </button>
          <button
            className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments ({payments.length})
          </button>
          <button
            className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline
          </button>
        </div>

        <div className="tab-content">
          {loadingTab ? (
            <div className="loading">Loading {activeTab}...</div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="overview-content">
                  <h3>Booking Information</h3>
                  <div className="info-grid">
                    <div>
                      <strong>Service Address:</strong>{' '}
                      {booking.serviceAddress
                        ? `${booking.serviceAddress.address}, ${booking.serviceAddress.city}, ${booking.serviceAddress.state} ${booking.serviceAddress.pincode}`
                        : 'N/A'}
                    </div>
                    {booking.distance && (
                      <div>
                        <strong>Distance:</strong> {booking.distance.toFixed(2)} km
                      </div>
                    )}
                    {booking.pricing.distanceBreakdown && (
                      <div>
                        <strong>Pricing Breakdown:</strong>
                        <div className="pricing-breakdown">
                          <div>Base Rate: ₹{booking.pricing.distanceBreakdown.baseRate.toLocaleString()}</div>
                          <div>Distance Charge: ₹{booking.pricing.distanceBreakdown.distanceCharge.toLocaleString()}</div>
                          <div>Transport Charge: ₹{booking.pricing.distanceBreakdown.transportCharge.toLocaleString()}</div>
                          <div>Total: ₹{booking.pricing.distanceBreakdown.total.toLocaleString()}</div>
                        </div>
                      </div>
                    )}
                    {booking.recurringConfig && (
                      <div>
                        <strong>Recurring:</strong> {booking.recurringConfig.frequency}
                        {booking.recurringConfig.endDate && (
                          <div>End Date: {formatDate(booking.recurringConfig.endDate)}</div>
                        )}
                        {booking.recurringConfig.occurrences && (
                          <div>Occurrences: {booking.recurringConfig.occurrences}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'negotiations' && (
                <div className="negotiations-content">
                  <h3>Booking Negotiations</h3>
                  {negotiation ? (
                    <div className="data-item">
                      <div className="data-item-header">
                        <strong>Negotiation Details</strong>
                      </div>
                      <div className="data-item-body">
                        <pre>{JSON.stringify(negotiation, null, 2)}</pre>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">No negotiations found for this booking</div>
                  )}
                </div>
              )}

              {activeTab === 'payments' && (
                <div className="payments-content">
                  <h3>Booking Payments</h3>
                  {payments.length === 0 ? (
                    <div className="empty-state">No payments found</div>
                  ) : (
                    <div className="data-list">
                      {payments.map((payment: Record<string, unknown>, index) => (
                        <div key={String(payment.paymentId ?? index)} className="data-item">
                          <div className="data-item-header">
                            <strong>Payment ID:</strong> {String(payment.paymentId ?? 'N/A')}
                          </div>
                          <div className="data-item-body">
                            <pre>{JSON.stringify(payment, null, 2)}</pre>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="timeline-content">
                  <h3>Booking Timeline</h3>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-date">{formatDate(booking.createdAt)}</div>
                      <div className="timeline-event">Booking Created</div>
                    </div>
                    {booking.status !== 'pending' && (
                      <div className="timeline-item">
                        <div className="timeline-date">{formatDate(booking.updatedAt)}</div>
                        <div className="timeline-event">Status: {booking.status}</div>
                      </div>
                    )}
                    {booking.scheduledDate && (
                      <div className="timeline-item">
                        <div className="timeline-date">{formatDate(booking.scheduledDate)}</div>
                        <div className="timeline-event">Scheduled Date</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

